import React from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Check, Trash2, Minus, Plus } from 'lucide-react';
import { Button } from '../ui/button';
import { getImageUrl } from '../../utils/imageUtils';
import { formatSellingUnit } from '../../pages/admin-products/catalogUnits';

const formatPrice = (price) =>
  new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(price);

const getItemLabel = (count) => (count === 1 ? 'item' : 'items');

/**
 * Desktop cart icon + mini-cart preview + recently-added popup.
 * All state stays in the parent; this component is pure presentation.
 */
const CartDropdown = ({
  cart,
  cartCount,
  cartTotal,
  hasItems,
  recentlyAdded,
  cartBounce,
  showMiniCart,
  setShowMiniCart,
  removeFromCart,
  updateQuantity,
}) => {
  return (
    <div
      className="relative"
      onMouseEnter={() => !recentlyAdded && setShowMiniCart(true)}
      onMouseLeave={() => setShowMiniCart(false)}
    >
      <Link to="/cart">
        <Button
          variant="ghost"
          className={`relative text-gray-700 hover:text-green-700 hover:bg-green-50 transition-all duration-300 ${
            cartBounce ? 'animate-bounce' : ''
          } ${hasItems ? 'bg-green-50' : ''}`}
          data-testid="cart-button"
        >
          <ShoppingCart className={`h-5 w-5 ${hasItems ? 'text-green-600' : ''}`} />
          {cartCount > 0 && (
            <span className={`absolute -top-1 -right-1 bg-green-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold shadow-lg ${
              cartBounce ? 'scale-125' : ''
            } transition-transform duration-300`}>
              {cartCount > 99 ? '99+' : cartCount}
            </span>
          )}
        </Button>
      </Link>

      {/* Recently Added Notification Popup */}
      {recentlyAdded && (
        <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-green-100 p-3 animate-in slide-in-from-top-2 duration-300 z-50">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
              <Check className="h-4 w-4 text-green-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">Added to cart!</p>
              <p className="text-xs text-gray-500 truncate">{recentlyAdded.name} × {recentlyAdded.quantity}</p>
            </div>
          </div>
          <Link to="/cart" className="block mt-2">
            <Button size="sm" className="w-full bg-green-600 hover:bg-green-700 text-white">
              View Cart ({cartCount} {getItemLabel(cartCount)})
            </Button>
          </Link>
        </div>
      )}

      {/* Mini Cart Preview on Hover */}
      {showMiniCart && hasItems && !recentlyAdded && (
        <div
          className="absolute top-full right-0 mt-2 w-80 bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200 z-50"
          data-testid="mini-cart"
        >
          <div className="bg-green-50 px-4 py-3 border-b">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <ShoppingCart className="h-4 w-4 text-green-600" />
              Your Cart ({cartCount} {getItemLabel(cartCount)})
            </h3>
          </div>

          <div className="max-h-64 overflow-y-auto">
            {cart.slice(0, 5).map((item) => (
              <div key={item.id} className="flex items-center gap-3 p-3 border-b hover:bg-gray-50 transition-colors">
                <div className="w-14 h-14 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                  {item.imageUrl || item.image ? (
                    <img
                      src={getImageUrl(item.imageUrl || item.image)}
                      alt={item.name}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <ShoppingCart className="h-6 w-6" />
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{item.name}</p>
                  <p className="text-xs text-gray-500">{formatPrice(item.price)} per {formatSellingUnit(item.unit_value, item.unit)}</p>

                  <div className="flex items-center gap-2 mt-1">
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); updateQuantity(item.id, item.quantity - 1); }}
                      className="w-6 h-6 rounded bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600 transition-colors"
                    >
                      <Minus className="h-3 w-3" />
                    </button>
                    <span className="text-sm font-medium w-8 text-center">{item.quantity}</span>
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); updateQuantity(item.id, item.quantity + 1); }}
                      className="w-6 h-6 rounded bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600 transition-colors"
                    >
                      <Plus className="h-3 w-3" />
                    </button>
                  </div>
                </div>

                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-semibold text-green-600">{formatPrice(item.price * item.quantity)}</p>
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); removeFromCart(item.id); }}
                    className="mt-1 p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
                    title="Remove item"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}

            {cart.length > 5 && (
              <div className="px-4 py-2 text-center text-sm text-gray-500 bg-gray-50">
                +{cart.length - 5} more items in cart
              </div>
            )}
          </div>

          <div className="bg-gray-50 p-4 border-t">
            <div className="flex items-center justify-between mb-3">
              <span className="text-gray-600">Subtotal:</span>
              <span className="text-lg font-bold text-green-600">{formatPrice(cartTotal)}</span>
            </div>
            <div className="flex gap-2">
              <Link to="/cart" className="flex-1">
                <Button variant="outline" size="sm" className="w-full">View Cart</Button>
              </Link>
              <Link to="/checkout" className="flex-1">
                <Button size="sm" className="w-full bg-green-600 hover:bg-green-700 text-white">Checkout</Button>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Empty Cart Preview */}
      {showMiniCart && !hasItems && !recentlyAdded && (
        <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 p-6 animate-in fade-in slide-in-from-top-2 duration-200 z-50 text-center">
          <ShoppingCart className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600 font-medium">Your cart is empty</p>
          <p className="text-sm text-gray-400 mt-1">Add some products to get started</p>
          <Link to="/shop" className="block mt-3">
            <Button size="sm" className="w-full bg-green-600 hover:bg-green-700 text-white">Browse Shop</Button>
          </Link>
        </div>
      )}
    </div>
  );
};

export default CartDropdown;
